import Chat from './Chat/Chat'
import DayJS from '../../../../helpers/DayJS'
import { Diffusion } from './Diffusion'
import { DiffusionMessage } from '../../../../apis/api-egg'
import { Tabs } from '../../../../components/Tabs'
import styled from 'styled-components'
import { useEffect, useState, useRef } from 'react'
import { useZoomContext } from '../../../../context/ZoomContext'
import { useTranslation } from 'react-i18next'

export const Messages = () => {
    const { t } = useTranslation('sidebar')
    const { unReadMessages, setUnReadMessages } = useZoomContext()
    
    // Determine the initial tab based on which message type has unread messages
    const getInitialTab = (): 'chat' | 'diffusion' => {
        if (!unReadMessages) return 'chat';
        if (unReadMessages.chat) return 'chat';
        if (unReadMessages.diffusion) return 'diffusion';
        return 'chat'; // Default to chat if no unread messages
    }
    
    const [tab, setTab] = useState<'chat' | 'diffusion'>(getInitialTab());

    // Track processed tabs to avoid infinite loops
    const processedTabs = useRef<Record<string, boolean>>({});

    // Clear unread status when tab changes or component mounts
    useEffect(() => {
        // Skip if already processed this tab or if no unread messages
        if (processedTabs.current[tab] || !unReadMessages) return;
        
        // Mark this tab as processed
        processedTabs.current[tab] = true;
        
        setUnReadMessages(prev => {
            if (!prev) return false;
            
            const newState = { ...prev };
            // Clear the unread status for the current tab
            if (tab === 'chat') delete newState.chat;
            if (tab === 'diffusion') delete newState.diffusion;
            
            // If no unread messages left, set to false
            return Object.keys(newState).length > 0 ? newState : false;
        });
    }, [tab, setUnReadMessages]);

    // Handle tab change
    const handleTabChange = (newTab: 'chat' | 'diffusion') => {
        setTab(newTab);
        // Reset processed flag for the new tab to allow clearing its unread status
        processedTabs.current[newTab] = false;
    };

    return (
        <>
            <Tabs
                tabs={[
                    { label: t('messages.chat'), value: 'chat' },
                    { label: t('messages.diffusion'), value: 'diffusion' },
                ]}
                defaultSelected={getInitialTab()}
                onChange={handleTabChange}
            />
            {tab === 'chat' ? <Chat /> : <Diffusion />}
        </>
    )
}

export const MessageHeader = ({ timestamp }: DiffusionMessage) => {
    const { t } = useTranslation('sidebar')
    return (
        <header className="message-header">
            <b>{t('messages.coach')}</b>
            <p className="to">{t('messages.to')}</p>
            <p className="receiver">{t('messages.everyone')}</p>
            <small>
                {DayJS(timestamp).format('HH:mm')} {t('messages.timeUnit')}
            </small>
        </header>
    )
}

export const MessagesStyled = styled.section`
    display: flex;
    flex-direction: column;
    // height: 100%;
    flex:1;
    flex-grow:1;
    main {
        flex: 1;
        overflow-y: scroll;
        scroll-behavior: smooth;
        &::-webkit-scrollbar {
            display: none;
        }
    }
    form.input-block {
        position: relative;

        textarea {
            width: 100%;
            height: 100px;
            padding: 20px;
            box-sizing: border-box;
            margin: 0;
            border: none;
            outline: none;
            border-radius: 0 0 14px 14px;
            font-family: 'Poppins', sans-serif;
            resize: none;
            background-color: ${props => props.theme.color.sidebar.gray1};
            /* border: 1px solid ${props => props.theme.color.sidebar.gray2}; */
            color: white;
            border-top: none;
        }
        button {
            background: none;
            border: none;
            cursor: pointer;
            position: absolute;
            bottom: 15px;
            right: 10px;
            font-size: 22px;
            color: ${props => props.theme.color.lightGray};
            &:disabled {
                opacity: 0.5;
                cursor: not-allowed;
            }
            &:hover {
                color: ${props => props.theme.color.electricBlue};
            }
        }
    }
`
